import axios, { AxiosResponse } from "axios";
import { setError } from "../store/slices/errorSlice";
import { AnyAction, Dispatch } from "redux";
import { hideLoader, showLoader } from "../store/slices/loadingSlice";
import { setLogin } from "../store/slices/loginSlice";
import { TokenManager } from "./TokenManager";
import { clearAuthStorage } from "./storageUtils";


const instance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 300000,
  withCredentials: true,
});

axios.defaults.withCredentials = true;

let apicallCount = 0;
let apicallCountForPost = 0;


export async function get(
  dispatch: Dispatch<AnyAction>,
  url: string,
  input: any
): Promise<any> {
  const isExpire = await TokenManager.checkAndRedirectIfExpired();
  const header = {
    access_token: localStorage.getItem("token"),
    tenantId: localStorage.getItem("tenantId"),
  };

  // const isExpire = checkExpTime();

  if (isExpire) {
    apicallCount++;
    dispatch(showLoader());
    return await instance
      .get(url + "?" + convertObjectToUrl(input), {
        headers: header,
      })
      .then((result) => {
        if (result?.data?.Success) {
          return result?.data?.Result;
        }
      })
      .catch((error) => {
        const err = {
          isError: true,
          errorMessage: error?.response?.data?.Error,
        };
        dispatch(setError(err));
      })
      .finally(() => {
        apicallCount--;
        if (apicallCount <= 0) {
          apicallCount = 0; // Ensure count never goes negative
          dispatch(hideLoader());
        }
      });
  } else {
    clearAuthStorage();
    dispatch(setLogin({ islogin: false }));
  }
}

export async function post(
  dispatch: Dispatch<AnyAction>,
  url: string,
  input: any
) {

  // const isExpire = checkExpTime();
  const isExpire = await TokenManager.checkAndRedirectIfExpired();

  const header = {
    access_token: localStorage.getItem("token"),
    tenantId: localStorage.getItem("tenantId"),
  };
  if (isExpire) {
    apicallCountForPost++;
    dispatch(showLoader());
    return await instance
      .post(url, input, {
        headers: header,
      })
      .then((result) => {
        if (result?.data?.Success) {
          return result?.data?.Result;
        }
        if (result?.data?.Error) {
          const err = {
            isError: true,
            errorMessage: result?.data?.Error,
          };
          dispatch(setError(err));
        }
      })
      .catch((error) => {
        const err = {
          isError: true,
          errorMessage: error?.response?.data?.Error,
        };
        dispatch(setError(err));
      })
      .finally(() => {
        apicallCountForPost--;
        if (apicallCountForPost <= 0) {
          apicallCountForPost = 0; // Ensure count never goes negative
          dispatch(hideLoader());
        }
      });
  } else {
    clearAuthStorage();
    dispatch(setLogin({ islogin: false }));
  }
}

export async function fetchSSE(
  dispatch: any,
  url: string,
  input: any,
  onMessage: (msg: { status: string; message: string }) => void
): Promise<void> {
  try {
    const header = {

      access_token: localStorage.getItem("token"),
      tenantId: localStorage.getItem("tenantId"),
    };
    // Show loader when starting the request
    dispatch(showLoader());
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}${url}`, {
      method: "POST",
      headers: {
        ...header,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        "Cache-Control": "no-cache",
        Connection: "keep-alive",
        "Upgrade-Insecure-Requests": "1",
      } as Record<string, string>,
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error("Failed to get response body");
    }

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      // Process each chunk as it arrives
      const messages = value.split("\n\n").filter(Boolean);
      for (const message of messages) {
        try {
          const jsonStr = message.replace("data: ", "").trim();
          const msg = JSON.parse(jsonStr);
          onMessage(msg); // Process message immediately
        } catch (e) {
          console.error("Error parsing message:", message, e);
        }
      }
    }
  } catch (error) {
    console.error("SSE Error:", error);
    throw error; // Re-throw to handle in calling function
  } finally {
    // Hide loader when complete or on error
    dispatch(hideLoader());
  }
}
export async function patch(
  dispatch: Dispatch<AnyAction>,
  url: string,
  input: any,
  param?: number | null
) {

  // const isExpire = checkExpTime();
  const isExpire = await TokenManager.checkAndRedirectIfExpired();
  const header = {
    access_token: localStorage.getItem("token"),
    tenantId: localStorage.getItem("tenantId"),
  };
  if (isExpire) {
    dispatch(showLoader());

    let urlForPatch = url;
    if (param) {
      urlForPatch = url + "/" + param.toString();
    }
    return await instance
      .patch(urlForPatch, input, {
        headers: header,
      })
      .then((result) => {
        if (result?.data?.Success) {
          return result?.data?.Result;
        }
      })
      .catch((error) => {
        const err = {
          isError: true,
          errorMessage: error?.response?.data?.Error,
        };
        dispatch(hideLoader());
        dispatch(setError(err));
      });
  } else {
    clearAuthStorage();
    dispatch(setLogin({ islogin: false }));
  }
}

export async function del(
  dispatch: Dispatch<AnyAction>,
  url: string,
  param: number
) {

  // const isExpire = checkExpTime();
  const isExpire = await TokenManager.checkAndRedirectIfExpired();
  const header = {
    access_token: localStorage.getItem("token"),
    tenantId: localStorage.getItem("tenantId"),
  };
  if (isExpire) {
    dispatch(showLoader());
    return await instance
      .delete(url + "/" + param?.toString(), {
        headers: header,
      })
      .then((result) => {
        if (result?.data?.Success) {
          return result?.data?.Result;
        }
      })
      .catch((error) => {
        const err = {
          isError: true,
          errorMessage: error?.response?.data?.Error,
        };
        dispatch(hideLoader());
        dispatch(setError(err));
      });
  } else {
    clearAuthStorage();
    dispatch(setLogin({ islogin: false }));
  }
}

export function convertObjectToUrl(input: any): string {
  let params = new URLSearchParams();
  for (let key in input) {
    params.set(key, input[key]);
  }
  return params.toString();
}

export default instance;
